import { NodeType, type CanvasNode, type NodeData } from '../types';

/**
 * The Image Ref node (IMAGE_VIEWER) is retired: the Reference node holds a picture, a video
 * or audio. Workspaces saved with Image Ref nodes still open; each becomes a Reference node
 * with the same picture or video, name, place, size and connections.
 */

/** Titles the old node gave itself; anything else was the user's name for it. */
const DEFAULT_TITLES = new Set(['Image Ref', 'Ref', 'Image', 'Image Reference', 'Reference']);

/** An Image Ref node as a Reference node; any other node as it is. */
export const toReferenceNode = (node: CanvasNode): CanvasNode => {
    if (node.type !== NodeType.IMAGE_VIEWER) return node;
    const data: NodeData = { ...node.data };
    const kind = data.imageUrl ? 'image' : data.videoUri ? 'video' : undefined;
    const title = (data.title || '').trim();
    const given = !!title && !DEFAULT_TITLES.has(title);
    return {
        ...node,
        type: NodeType.MEDIA_REFERENCE,
        data: {
            ...data,
            ...(kind ? { referenceKind: kind } : {}),
            ...(kind === 'video' && !data.videoFileName && given ? { videoFileName: title } : {}),
            title: given ? title : 'Reference',
            // A name the user gave stays when the media changes, as on any Reference node
            ...(given && !/\.[a-z0-9]{2,4}$/i.test(title) ? { referenceNamed: true } : {}),
        },
    };
};

/** The nodes with any Image Ref nodes turned into Reference nodes; the same array when there are none. */
export const migrateLegacyNodes = (nodes: CanvasNode[]): CanvasNode[] =>
    nodes.some(node => node.type === NodeType.IMAGE_VIEWER) ? nodes.map(toReferenceNode) : nodes;

export const hasLegacyNodes = (nodes: CanvasNode[]): boolean => nodes.some(node => node.type === NodeType.IMAGE_VIEWER);
